import { differenceInCalendarDays, startOfDay } from 'date-fns';
import { resolvePaymentLifecycleGroup, type CommunityPaymentStatus } from '@/constants/communityPayments';
import { Alert } from '@/types/dashboard';
import { PaymentSearchPayload } from '@/types/paymentSearch';
import { SupportRequest } from '@/types/support';
import {
  cloneOldSlotsByDay,
  cloneSlotsByDay,
} from '@/utils/commonAreaSlots';
import { CommonArea, Community, Forum, Incident, Message, Payment, Poll, Reservation, User } from '../types';

type NormalizedPaymentAttachment = {
  name: string;
  url: string;
  path?: string;
};

type PaymentUpdateInput = Partial<{
  description: string;
  amount: number;
  dueDate: string;
  paidDate: string | null;
  type: Payment['type'];
  status: Payment['status'];
  attachments: NormalizedPaymentAttachment[];
}>;

export type CreateLevyActionPayload = {
  communityId: string;
  amount: number;
  dueDate: string;
  description?: string;
};

type IncidentDeletionValidation =
  | {
      canDelete: true;
      incident: Incident;
      isAppAdmin: boolean;
    }
  | {
      canDelete: false;
      title: string;
      description: string;
    };

const toCommunityPaymentStatus = (
  status: Payment['status'] | null | undefined,
): CommunityPaymentStatus | null => (status && status !== 'pending' ? status : null);

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
    : [];

const sortCommunitiesByName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));

export const prependUniqueById = <T extends { id: string }>(items: T[], item: T): T[] => [
  item,
  ...items.filter((current) => current.id !== item.id),
];

export const replaceById = <T extends { id: string }>(items: T[], item: T): T[] =>
  items.map((current) => (current.id === item.id ? item : current));

export const removeById = <T extends { id: string }>(items: T[], id: string): T[] =>
  items.filter((item) => item.id !== id);

export const buildPaymentAttachmentPath = (
  communityId: string,
  userId: string,
  paymentId: string,
  fileName: string,
): string => {
  const normalizedName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  return `${communityId}/payments/${userId}/${paymentId}/${normalizedName}`;
};

export const normalizePaymentAttachments = (
  payment: Pick<Payment, 'attachments' | 'communityId' | 'userId'>,
  paymentId: string,
): NormalizedPaymentAttachment[] =>
  (payment.attachments ?? []).map((attachment) => ({
    name: attachment.name,
    url: attachment.url,
    path:
      attachment.path && attachment.path.length > 0
        ? attachment.path
        : buildPaymentAttachmentPath(
            payment.communityId,
            payment.userId,
            paymentId,
            attachment.name,
          ),
  }));

export const isCompletedPayment = (payment: Pick<Payment, 'status'>): boolean =>
  resolvePaymentLifecycleGroup(toCommunityPaymentStatus(payment.status)) === 'completed';

export const resolvePaidDate = (payment: Pick<Payment, 'paidDate'>): string => {
  if (payment.paidDate) {
    return payment.paidDate;
  }

  const today = new Date();
  return new Date(today.getTime() - today.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

export const toPaymentUpdateInput = (
  updates: Partial<Omit<Payment, 'id' | 'communityId'>>,
): PaymentUpdateInput => ({
  description: updates.description,
  amount: updates.amount,
  dueDate: updates.dueDate,
  paidDate: updates.paidDate ?? null,
  type: updates.type,
  status: updates.status,
  attachments: updates.attachments?.map((attachment) => ({
    name: attachment.name,
    url: attachment.url,
    path: attachment.path ?? undefined,
  })),
});

export const filterOverduePayments = (
  payments: Payment[],
  options: { communityId?: string; minOverdueDays?: number },
): Payment[] => {
  const { communityId, minOverdueDays = 0 } = options;
  const now = new Date();

  return payments.filter((payment) => {
    if (communityId && payment.communityId !== communityId) {
      return false;
    }

    if (isCompletedPayment(payment)) {
      return false;
    }

    const dueDate = new Date(payment.dueDate);
    const daysOverdue = Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysOverdue >= minOverdueDays;
  });
};

export const filterPaymentsForReceipts = (
  payments: Payment[],
  options: { userId?: string; communityId?: string },
): Payment[] =>
  payments.filter((payment) => {
    if (options.userId && payment.userId !== options.userId) {
      return false;
    }

    if (options.communityId && payment.communityId !== options.communityId) {
      return false;
    }

    return true;
  });

export const buildPaymentsCsv = (payments: Payment[]): string => {
  const header = 'ID,Usuario,Comunidad,Monto,Estado,Fecha de vencimiento\n';
  const rows = payments
    .map(
      (payment) =>
        `${payment.id},${payment.userId},${payment.communityId},${payment.amount},${payment.status ?? ''},${payment.dueDate}`,
    )
    .join('\n');

  return header + rows;
};

export const buildLevyPayments = (
  community: Community,
  input: { amount: number; communityId: string; description?: string; dueDate: string },
  generateId: () => string,
): Payment[] =>
  community.members.map((member) => ({
    id: generateId(),
    userId: member.id,
    communityId: input.communityId,
    amount: input.amount,
    status: 'pending_collection',
    type: 'levy',
    dueDate: input.dueDate,
    description: input.description || 'Cuota comunitaria',
  }));

export const validateIncidentDeletion = (
  currentUser: User | null,
  incident: Incident | undefined,
): IncidentDeletionValidation => {
  if (!currentUser) {
    return {
      canDelete: false,
      title: 'Usuario no autenticado',
      description: 'Debes iniciar sesión para eliminar una incidencia.',
    };
  }

  if (!incident) {
    return {
      canDelete: false,
      title: 'Incidencia no encontrada',
      description: 'No se encontró la incidencia que intentas eliminar.',
    };
  }

  const isAppAdmin = currentUser.role === 'admin';
  if (!isAppAdmin && incident.reportedById !== currentUser.id) {
    return {
      canDelete: false,
      title: 'Acceso restringido',
      description: 'Solo el creador de la incidencia puede eliminarla.',
    };
  }

  if (!isAppAdmin && incident.status !== 'pending') {
    return {
      canDelete: false,
      title: 'No se puede eliminar la incidencia',
      description: 'Solo puedes eliminar incidencias que estén pendientes.',
    };
  }

  return {
    canDelete: true,
    incident,
    isAppAdmin,
  };
};

export const canDeletePayment = (currentUser: User | null): boolean => currentUser?.role === 'manager';

export const hasStringArrayField = <K extends string>(
  value: PaymentSearchPayload | undefined,
  key: K,
): value is PaymentSearchPayload & Record<K, string[]> =>
  Array.isArray(value?.[key]) && value[key].every((item) => typeof item === 'string');

export const isCreateLevyActionPayload = (
  value: PaymentSearchPayload | undefined,
): value is CreateLevyActionPayload => {
  if (!value) {
    return false;
  }

  return (
    typeof value.communityId === 'string' &&
    typeof value.amount === 'number' &&
    Number.isFinite(value.amount) &&
    typeof value.dueDate === 'string' &&
    (value.description === undefined || typeof value.description === 'string')
  );
};

export const applyCommunityHousingConfig = (
  communities: Community[],
  communityId: string,
  updated: Partial<Pick<Community, 'blocks' | 'floors' | 'letters'>>,
): Community[] =>
  sortCommunitiesByName(
    communities.map((community) =>
      community.id === communityId
        ? {
            ...community,
            blocks: toStringArray(updated.blocks),
            floors: toStringArray(updated.floors),
            letters: toStringArray(updated.letters),
          }
        : community,
    ),
  );

export const createPollRecord = (
  poll: Omit<Poll, 'id'>,
  generateId: () => string,
): Poll => ({
  ...poll,
  id: generateId(),
  voters: Array.isArray(poll.voters) ? poll.voters : [],
});

export const addPollVote = (
  polls: Poll[],
  input: { optionId: string; pollId: string; userId: string },
): Poll[] =>
  polls.map((poll) => {
    if (poll.id !== input.pollId) {
      return poll;
    }

    const voters = poll.voters ?? [];
    if (voters.includes(input.userId)) {
      return poll;
    }

    return {
      ...poll,
      options: poll.options.map((option) =>
        option.id === input.optionId ? { ...option, votes: option.votes + 1 } : option,
      ),
      voters: [...voters, input.userId],
    };
  });

export const closePollById = (polls: Poll[], id: string): Poll[] =>
  polls.map((poll) => (poll.id === id ? { ...poll, isActive: false } : poll));

const isReservationActive = (reservation: Reservation): boolean => reservation.status !== 'cancelled';

export const findReservationConflict = (
  dayReservations: Reservation[],
  candidate: Pick<Reservation, 'endTime' | 'startTime'>,
  normalizeTime: (value: string) => string,
  overlaps: (startA: string, endA: string, startB: string, endB: string) => boolean,
): boolean => {
  const start = normalizeTime(candidate.startTime);
  const end = normalizeTime(candidate.endTime);

  return dayReservations.some((reservation) =>
    overlaps(start, end, normalizeTime(reservation.startTime), normalizeTime(reservation.endTime)),
  );
};

export const createReservationRecord = (
  reservation: Omit<Reservation, 'id' | 'createdAt' | 'status'>,
  options: {
    generateId: () => string;
    getCurrentDate: () => string;
    normalizeTime: (value: string) => string;
  },
): Reservation => ({
  ...reservation,
  id: options.generateId(),
  createdAt: options.getCurrentDate(),
  status: 'confirmed',
  startTime: options.normalizeTime(reservation.startTime),
  endTime: options.normalizeTime(reservation.endTime),
});

export const canUserReserveArea = (
  reservations: Reservation[],
  area: CommonArea | undefined,
  input: { areaId: string; date: string; userId: string },
): boolean => {
  const maxPerDay = area?.maxReservationsPerDay ?? 1;
  const today = startOfDay(new Date());
  const targetDate = startOfDay(new Date(`${input.date}T00:00:00`));

  if (Number.isNaN(targetDate.getTime())) {
    return false;
  }

  const hasFutureLimit =
    typeof area?.maxFutureDays === 'number' && Number.isFinite(area.maxFutureDays);
  if (hasFutureLimit) {
    const maxFutureDays = area.maxFutureDays ?? 0;
    const normalizedMaxFutureDays = Math.max(0, Math.floor(maxFutureDays));
    if (differenceInCalendarDays(targetDate, today) > normalizedMaxFutureDays) {
      return false;
    }
  }

  if (maxPerDay <= 0) {
    return true;
  }

  const activeReservations = reservations.filter(
    (reservation) =>
      reservation.userId === input.userId &&
      reservation.commonAreaId === input.areaId &&
      isReservationActive(reservation),
  );

  if (area?.absLimit) {
    const now = new Date();
    const pendingReservations = activeReservations.filter((reservation) => {
      const timeLabel = reservation.endTime || reservation.startTime;
      if (!reservation.date || !timeLabel) {
        return true;
      }

      const [year, month, day] = reservation.date.split('-').map(Number);
      const [hours, minutes] = timeLabel.split(':').map(Number);
      if ([year, month, day, hours, minutes].some((value) => value === undefined || Number.isNaN(value))) {
        return true;
      }

      const endDate = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1, hours ?? 0, minutes ?? 0, 0));
      return endDate.getTime() > now.getTime();
    });

    return pendingReservations.length < maxPerDay;
  }

  const sameDayReservations = activeReservations.filter(
    (reservation) => reservation.date === input.date,
  );
  return sameDayReservations.length < maxPerDay;
};

export const cancelReservationById = (reservations: Reservation[], id: string): Reservation[] =>
  reservations.map((reservation) =>
    reservation.id === id ? { ...reservation, status: 'cancelled' } : reservation,
  );

export const createMessageRecord = (
  message: Omit<Message, 'id' | 'createdAt' | 'isRead'>,
  options: { generateId: () => string; getCurrentDate: () => string },
): Message => ({
  ...message,
  id: options.generateId(),
  createdAt: options.getCurrentDate(),
  isRead: false,
});

export const createForumRecord = (
  forum: Omit<Forum, 'id' | 'createdAt'>,
  options: { generateId: () => string; getCurrentDate: () => string },
): Forum => ({
  ...forum,
  id: options.generateId(),
  createdAt: options.getCurrentDate(),
});

export const upsertCommonArea = (
  areas: CommonArea[],
  id: string,
  updates: Partial<CommonArea>,
): CommonArea[] => {
  const matchesCommunity = (area: CommonArea) => {
    if (updates.communityId !== undefined) {
      return area.id === id && area.communityId === updates.communityId;
    }

    return area.id === id;
  };

  const index = areas.findIndex(matchesCommunity);
  if (index === -1) {
    if (updates.communityId === undefined) {
      return areas;
    }

    const newArea: CommonArea = {
      id,
      name: updates.name ?? 'Área sin nombre',
      description: updates.description,
      communityId: updates.communityId ?? '',
      communityIds: updates.communityIds ?? (updates.communityId ? [updates.communityId] : []),
      image: updates.image,
      maxReservationsPerDay: updates.maxReservationsPerDay,
      maxFutureDays: updates.maxFutureDays ?? null,
      absLimit: updates.absLimit ?? false,
      uniqueHouse: updates.uniqueHouse ?? false,
      slotsByDay: cloneSlotsByDay(updates.slotsByDay),
      oldSlotsByDay: cloneOldSlotsByDay(updates.oldSlotsByDay),
      slotsApplyDate: updates.slotsApplyDate ?? null,
      blockedDays: updates.blockedDays ?? [],
      reservationPayment: updates.reservationPayment ?? false,
      onlyRenter: updates.onlyRenter ?? false,
      lights: updates.lights ?? false,
      ssid: updates.ssid ?? null,
    };

    return [...areas, newArea];
  }

  const next = [...areas];
  next[index] = {
    ...next[index],
    ...updates,
    slotsByDay: updates.slotsByDay ? cloneSlotsByDay(updates.slotsByDay) : next[index].slotsByDay,
    oldSlotsByDay: updates.oldSlotsByDay
      ? cloneOldSlotsByDay(updates.oldSlotsByDay)
      : next[index].oldSlotsByDay,
  };

  return next;
};

export const removeCommonAreaByScope = (
  areas: CommonArea[],
  id: string,
  communityId?: string,
): CommonArea[] =>
  areas.filter((area) => {
    if (communityId !== undefined) {
      return !(area.id === id && area.communityId === communityId);
    }

    return area.id !== id;
  });

export const buildSupportRequestAlert = (request: SupportRequest): Alert => {
  const isIncident = request.type === 'incidencia';
  const messagePreview = (request.description ?? '').slice(0, 80);

  return {
    id: `support-${request.id}`,
    type: isIncident ? 'IncidenciaUrgente' : 'General',
    message: `Nueva solicitud de soporte: ${messagePreview}...`,
    date: new Date().toISOString(),
    criticalityLevel: isIncident ? 'alta' : 'media',
    communityId: request.communityId,
    relatedEntityId: String(request.id),
    relatedEntityType: 'supportRequest',
  };
};
